import React from 'react';
import { useTranslation } from 'react-i18next';

function HeroFloatSparkline({ className = '', id = 'heroSparkFill' }) {
  return (
    <svg
      viewBox="0 0 120 36"
      className={className}
      aria-hidden
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(113,75,103,0.2)" />
          <stop offset="100%" stopColor="rgba(113,75,103,0)" />
        </linearGradient>
      </defs>
      <path
        d="M0 28 L18 24 L32 26 L48 16 L64 18 L80 8 L96 12 L120 4"
        stroke="rgba(113,75,103,0.5)"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0 28 L18 24 L32 26 L48 16 L64 18 L80 8 L96 12 L120 4 V36 H0 Z"
        fill={`url(#${id})`}
      />
      <circle cx="80" cy="8" r="3.5" fill="#714b67" stroke="white" strokeWidth="1.75" />
    </svg>
  );
}

function HeroFloatBars({ className = '' }) {
  const bars = [12, 20, 16, 26, 18, 24];
  return (
    <div className={`flex items-end justify-end gap-[3px] ${className}`} aria-hidden>
      {bars.map((h, i) => (
        <span
          key={i}
          className="w-[5px] rounded-full bg-gradient-to-t from-signal/25 to-signal/65"
          style={{ height: `${h}px`, opacity: 0.55 + i * 0.07 }}
        />
      ))}
    </div>
  );
}

export default function LandingFloatingCard({
  variant = 'feature',
  statKey,
  labelKey,
  titleKey,
  descKey,
  icon: Icon,
  position = '',
  delay = '0s',
  className = '',
  sparkId = 'heroSparkFill',
}) {
  const { t } = useTranslation();

  const baseClass =
    'landing-float-card pointer-events-none absolute z-20 hidden lg:block';

  if (variant === 'stat') {
    return (
      <div
        className={`${baseClass} landing-float-card--stat ${position} ${className}`}
        style={{ animationDelay: delay }}
        aria-hidden
      >
        <div className="landing-float-card-inner">
          <div className="mb-4 flex items-start justify-between gap-3">
            <span className="landing-float-chip">{t('hero.stats.productsBadge', { defaultValue: 'ویترین' })}</span>
            {Icon ? (
              <span className="landing-float-icon landing-float-icon--sm shrink-0">
                <Icon className="h-4 w-4" strokeWidth={2.1} aria-hidden />
              </span>
            ) : null}
          </div>
          <p className="landing-float-stat">{t(statKey)}</p>
          <p className="landing-float-label mt-2.5 max-w-[9.5rem]">{t(labelKey)}</p>
        </div>
      </div>
    );
  }

  if (variant === 'visual') {
    return (
      <div
        className={`${baseClass} landing-float-card--visual ${position} ${className}`}
        style={{ animationDelay: delay }}
        aria-hidden
      >
        <div className="landing-float-card-inner">
          <div className="mb-2.5 flex items-start justify-between gap-2">
            <p className="landing-float-title flex-1">{t(titleKey || statKey)}</p>
            {Icon ? (
              <span className="landing-float-icon landing-float-icon--sm shrink-0">
                <Icon className="h-4 w-4" strokeWidth={2.1} aria-hidden />
              </span>
            ) : null}
          </div>
          <p className="landing-float-desc mb-3.5">{t(descKey || labelKey)}</p>
          <HeroFloatSparkline id={sparkId} className="h-8 w-full opacity-90" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${baseClass} landing-float-card--feature ${position} ${className}`}
      style={{ animationDelay: delay }}
      aria-hidden
    >
      <div className="landing-float-card-inner">
        <div className="mb-3 flex items-start justify-between gap-2">
          {Icon ? (
            <span className="landing-float-icon shrink-0">
              <Icon className="h-[17px] w-[17px]" strokeWidth={2.1} aria-hidden />
            </span>
          ) : (
            <span />
          )}
          {variant === 'feature-bars' ? <HeroFloatBars className="h-6 w-14" /> : null}
        </div>
        <p className="landing-float-title">{t(titleKey || statKey)}</p>
        <p className="landing-float-desc mt-1.5">{t(descKey || labelKey)}</p>
      </div>
    </div>
  );
}
